import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { Botao, Cartao, Esqueleto, TituloSecao } from '@/componentes/ui'
import { supabase } from '@/lib/supabase'
import { cn, mensagemDeErro } from '@/lib/utilitarios'
import { ROTULO_REFEICAO, TIPOS_REFEICAO, type TipoRefeicao } from '@/tipos/dominio'
import type { Cardapio, Refeicao } from '@/tipos/tabelas'
import { CHAVES_ADMIN, useProgramaAtual } from './comum'

type CardapioComRefeicoes = Cardapio & { refeicoes: Refeicao[] }

const PERIODOS = [
  { numero: 1, rotulo: 'Semanas 1 e 2' },
  { numero: 2, rotulo: 'Semanas 3 e 4' },
] as const

interface CamposLivres {
  chas: string
  lembretesImportantes: string
  alimentosProibidos: string
}

const CAMPOS_VAZIOS: CamposLivres = { chas: '', lembretesImportantes: '', alimentosProibidos: '' }

/**
 * Cardápios por período (seção 8).
 *
 * Texto simples, sem modo de preparo — a especificação é explícita em
 * não ter receitas dentro do app. Um cardápio por período de duas
 * semanas: o mesmo vale para as semanas 1-2, e outro para as 3-4, em
 * vez de quatro cardápios distintos — é como a Raquel varia o menu na
 * prática.
 */
export function SecaoCardapios() {
  const { data: programa } = useProgramaAtual()
  const clienteQuery = useQueryClient()
  const [periodo, setPeriodo] = useState(1)
  const [refeicoes, setRefeicoes] = useState<Partial<Record<TipoRefeicao, string>>>({})
  const [campos, setCampos] = useState<CamposLivres>(CAMPOS_VAZIOS)
  const [erro, setErro] = useState<string | null>(null)

  const { data: cardapio, isLoading } = useQuery({
    queryKey: CHAVES_ADMIN.cardapios(programa?.id ?? '', periodo),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cardapios')
        .select('*, refeicoes(*)')
        .eq('programa_id', programa?.id ?? '')
        .eq('periodo', periodo)
        .is('dia_numero', null)
        .maybeSingle()

      if (error) throw error
      return data as CardapioComRefeicoes | null
    },
    enabled: Boolean(programa),
  })

  // Sincroniza o rascunho com o que veio do banco, para a edição partir
  // do texto atual em vez de um campo em branco. Fica num efeito, e não
  // dentro do queryFn: o queryFn pode reexecutar em revalidação e
  // sobrescreveria o que a Raquel acabou de digitar.
  useEffect(() => {
    const inicial: Partial<Record<TipoRefeicao, string>> = {}
    for (const tipo of TIPOS_REFEICAO) {
      inicial[tipo] = cardapio?.refeicoes.find((r) => r.tipo === tipo)?.descricao ?? ''
    }
    setRefeicoes(inicial)

    setCampos({
      chas: cardapio?.chas ?? '',
      lembretesImportantes: cardapio?.lembretes_importantes ?? '',
      alimentosProibidos: cardapio?.alimentos_proibidos ?? '',
    })
  }, [cardapio])

  const salvar = useMutation({
    mutationFn: async () => {
      if (!programa) throw new Error('Nenhum programa ativo.')

      const camposCardapio = {
        chas: campos.chas.trim() || null,
        lembretes_importantes: campos.lembretesImportantes.trim() || null,
        alimentos_proibidos: campos.alimentosProibidos.trim() || null,
      }

      let cardapioId = cardapio?.id

      if (!cardapioId) {
        const rotulo = PERIODOS.find((p) => p.numero === periodo)?.rotulo ?? `Período ${periodo}`
        const { data, error } = await supabase
          .from('cardapios')
          .insert({ programa_id: programa.id, periodo, titulo: rotulo, ...camposCardapio })
          .select('id')
          .single()
        if (error) throw error
        cardapioId = (data as { id: string }).id
      } else {
        const { error } = await supabase.from('cardapios').update(camposCardapio).eq('id', cardapioId)
        if (error) throw error
      }

      // Substitui as refeições inteiras: são cinco linhas, e reconciliar
      // item a item custaria mais código do que vale.
      const { error: erroLimpeza } = await supabase
        .from('refeicoes')
        .delete()
        .eq('cardapio_id', cardapioId)
      if (erroLimpeza) throw erroLimpeza

      const novas = TIPOS_REFEICAO.map((tipo, indice) => ({
        cardapio_id: cardapioId,
        tipo,
        descricao: (refeicoes[tipo] ?? '').trim(),
        ordem: indice,
      })).filter((r) => r.descricao.length > 0)

      if (novas.length > 0) {
        const { error } = await supabase.from('refeicoes').insert(novas)
        if (error) throw error
      }
    },
    onSuccess: () => {
      setErro(null)
      void clienteQuery.invalidateQueries({
        queryKey: CHAVES_ADMIN.cardapios(programa?.id ?? '', periodo),
      })
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  })

  const rotuloPeriodo = PERIODOS.find((p) => p.numero === periodo)?.rotulo ?? ''

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-2">
        {PERIODOS.map((p) => (
          <button
            key={p.numero}
            type="button"
            onClick={() => setPeriodo(p.numero)}
            aria-current={periodo === p.numero ? 'true' : undefined}
            className={cn(
              'h-11 flex-1 rounded-full font-titulo text-sm font-semibold transition-colors',
              periodo === p.numero
                ? 'bg-dourado text-marrom'
                : 'bg-[var(--cartao)] text-[var(--tinta-suave)]',
            )}
          >
            {p.rotulo}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Esqueleto className="h-96" />
      ) : (
        <>
          <TituloSecao>Cardápio das {rotuloPeriodo.toLowerCase()}</TituloSecao>

          <div className="flex flex-col gap-4">
            {TIPOS_REFEICAO.map((tipo) => (
              <Cartao key={tipo}>
                <label className="block">
                  <span className="mb-2 block font-titulo text-sm font-semibold text-coral">
                    {ROTULO_REFEICAO[tipo]}
                  </span>
                  <textarea
                    rows={3}
                    value={refeicoes[tipo] ?? ''}
                    onChange={(e) => setRefeicoes((r) => ({ ...r, [tipo]: e.target.value }))}
                    placeholder="Sugestões para esta refeição, em texto simples."
                    className="w-full resize-none rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] p-3 text-[15px] leading-relaxed"
                  />
                </label>
              </Cartao>
            ))}

            <Cartao>
              <label className="block">
                <span className="mb-2 block font-titulo text-sm font-semibold text-verde">Chás</span>
                <textarea
                  rows={2}
                  value={campos.chas}
                  onChange={(e) => setCampos((c) => ({ ...c, chas: e.target.value }))}
                  placeholder="Chás recomendados neste período, e quando tomar cada um."
                  className="w-full resize-none rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] p-3 text-[15px] leading-relaxed"
                />
              </label>
            </Cartao>

            <Cartao>
              <label className="block">
                <span className="mb-2 block font-titulo text-sm font-semibold text-dourado">
                  Lembretes importantes
                </span>
                <textarea
                  rows={2}
                  value={campos.lembretesImportantes}
                  onChange={(e) =>
                    setCampos((c) => ({ ...c, lembretesImportantes: e.target.value }))
                  }
                  placeholder="Avisos gerais deste período — ajustes, cuidados, o que reforçar."
                  className="w-full resize-none rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] p-3 text-[15px] leading-relaxed"
                />
              </label>
            </Cartao>

            <Cartao>
              <label className="block">
                <span className="mb-2 block font-titulo text-sm font-semibold text-pink">
                  Alimentos proibidos
                </span>
                <textarea
                  rows={2}
                  value={campos.alimentosProibidos}
                  onChange={(e) => setCampos((c) => ({ ...c, alimentosProibidos: e.target.value }))}
                  placeholder="O que evitar neste período, um item por linha."
                  className="w-full resize-none rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] p-3 text-[15px] leading-relaxed"
                />
              </label>
            </Cartao>
          </div>

          {erro && (
            <p role="alert" className="rounded-2xl bg-pink/10 p-3 text-sm text-pink">
              {erro}
            </p>
          )}

          <Botao tamanho="bloco" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            Salvar cardápio das {rotuloPeriodo.toLowerCase()}
          </Botao>

          <p className="text-center text-xs text-[var(--tinta-apagada)]">
            Campos deixados em branco não aparecem para a participante.
          </p>
        </>
      )}
    </div>
  )
}
